import { createSelector } from 'reselect';
import permissionConstants from 'xm-constants/permissions';
import { hasPermission } from 'utils/permissions';
import {
  pruneItemsAndGroups,
  selectFeatureToggleMap,
  selectLoggedInUser,
  selectPermissions,
  selectSubMenuId,
} from './util';

export const getSignalsReportNavItem = createSelector(
  [selectPermissions],
  permissions => {
    const hasPermissionToViewSignals = hasPermission(
      permissions,
      permissionConstants.VIEW_MENU_DEVELOPER,
    );
    return (
      hasPermissionToViewSignals && {
        id: 'NAV_SIGNALS_REPORT',
        to: `reports/signals`,
        permission: permissionConstants.VIEW_MENUITEM_EVENT_ACTIVITY_REPORT,
        selectorId: 'SignalsReport',
      }
    );
  },
);

function getReportsNav(
  featureToggleMap,
  loggedInUser,
  subMenuId,
  signalsReportNavItem,
) {
  const { encryptedCompanyId } = loggedInUser;
  const { permissions } = loggedInUser.loggedInUserProfile;
  const hasPermissionViewCompanyReports = hasPermission(
    permissions,
    permissionConstants.VIEW_MENUITEM_COMPANY_REPORTS,
  );

  const isReactNotificationReportFlagEnabled =
    featureToggleMap.REACT_NOTIFICATIONS_REPORT === 'true';
  const isWorkflowsAuditReportFlagEnabled =
    featureToggleMap.WORKFLOWS_AUDIT_REPORT === 'true';
  const isComponentStatusSummaryReportEnabled =
    featureToggleMap.ENABLE_COMPONENT_STATUS_SUMMARY_REPORT === 'true';
  const isNewSystemAudit = featureToggleMap.USER_AUDIT_REPORT === 'true';

  const navItemGroups = [
    hasPermission(
      permissions,
      permissionConstants.VIEW_MENUITEM_REPORTS_ACTIVITY,
    ) && {
      id: 'NAV_ACTIVITY',
      items: [
        {
          id: 'NAV_ALL_ALERTS',
          to: `alerts`,
          permission: permissionConstants.VIEW_MENUITEM_EVENT_ACTIVITY_REPORT,
          selectorId: 'alerts',
        },
        {
          id: 'NAV_ALERT_VIEWS',
          to: `AlertViews`,
          permission: permissionConstants.VIEW_MENUITEM_EVENT_ACTIVITY_REPORT,
          selectorId: 'AlertViews',
        },
        isReactNotificationReportFlagEnabled
          ? {
              id: 'NAV_NOTIFICATIONS',
              to: 'notifications',
              permission: permissionConstants.VIEW_MENUITEM_NOTIFICATION_REPORT,
              selectorId: 'notifications',
            }
          : {
              id: 'NAV_NOTIFICATIONS',
              to: `CompanyNotificationReportPlace:orgId=${encryptedCompanyId}`,
              permission: permissionConstants.VIEW_MENUITEM_NOTIFICATION_REPORT,
              selectorId: 'CompanyNotificationReportPlace',
            },
        {
          id: 'NAV_CONFERENCES',
          to: `ConferencesReportPlace:none`,
          permission: permissionConstants.VIEW_MENUITEM_EVENT_ACTIVITY_REPORT,
          selectorId: 'ConferencesReportPlace',
        },
        signalsReportNavItem,
      ],
    },
    hasPermission(
      permissions,
      permissionConstants.VIEW_MENUITEM_SYSTEM_REPORTS,
    ) && {
      id: 'NAV_SYSTEM_REPORTS',
      items: [
        isComponentStatusSummaryReportEnabled && {
          id: 'NAV_COMPONENT_STATUS_SUMMARY_REPORT',
          to: 'CocoonPlace:/xmatters/Reporting/ComponentStatus/ComponentStatusSummaryReport.do',
          permission:
            permissionConstants.VIEW_MENUITEM_COMPONENT_STATUS_SUMMARY_REPORT,
          isActive: () => subMenuId === 'ComponentStatus', // Match all subroutes
          selectorId: 'ComponentStatus',
        },
        {
          id: 'NAV_SYSTEM_NOTIFICATION_REPORT',
          to: 'SystemNotificationReportPlace:',
          permission:
            permissionConstants.VIEW_MENUITEM_SYSTEM_NOTIFICATION_REPORT,
          isActive: () => subMenuId === 'SystemNotificationReportPlace', // Match all subroutes
          selectorId: 'SystemNotificationReportPlace',
        },
        {
          id: 'NAV_NOTIFICATION_THROUGHPUT',
          to: `ThroughputHistoryReportPlace:userTime=${Date.now()}`,
          permission: permissionConstants.VIEW_MENUITEM_NOTIFICATION_THROUGHPUT,
          isActive: () => subMenuId === 'ThroughputHistoryReportPlace', // Match all subroutes
          selectorId: 'ThroughputHistoryReportPlace',
        },
        {
          id: 'NAV_SYNCHRONIZATION_REPORT',
          to: 'EpicStatusReportPlace:all',
          permission:
            permissionConstants.VIEW_MENUITEM_EPICSTATUS_REPORT_OPERATIONS,
          isActive: () => subMenuId === 'EpicStatusReportPlace', // Match all subroutes
          selectorId: 'EpicStatusReportPlace',
        },
        {
          id: 'NAV_EPIC_STATUS_REPORT',
          to: 'EPICSyncReportPlace:all',
          permission:
            permissionConstants.VIEW_MENUITEM_EPICSTATUS_REPORT_OPERATIONS,
          isActive: () => subMenuId === 'EPICSyncReportPlace', // Match all subroutes
          selectorId: 'EPICSyncReportPlace',
        },
      ],
    },
    hasPermissionViewCompanyReports && {
      id: 'NAV_COMPANY_REPORTS',
      items: [
        {
          id: 'NAV_SYNCHRONIZATION_REPORT',
          to: 'EpicStatusReportPlace:all',
          permission: permissionConstants.VIEW_MENUITEM_EPICSTATUS_REPORT,
          selectorId: 'EpicStatusReportPlace',
        },
        {
          id: 'NAV_IMPORT_JOB_HISTORY_REPORT',
          to: `ImportJobHistoryReportPlace:none`,
          permission:
            permissionConstants.VIEW_MENUITEM_IMPORT_JOB_HISTORY_REPORT,
          selectorId: 'ImportJobHistoryReportPlace',
        },
      ],
    },
    hasPermissionViewCompanyReports && {
      id: 'NAV_AUDIT',
      items: [
        !isNewSystemAudit && {
          id: 'NAV_SYSTEM_AUDIT_REPORT',
          to: 'CocoonPlace:/xmatters/Reporting/ApplicationAuditReport/ApplicationAuditReport.do',
          permission: permissionConstants.VIEW_MENUITEM_SYSTEM_AUDIT_REPORT,
          isActive: () => subMenuId === 'ApplicationAuditReport', // Match all subroutes
          selectorId: 'ApplicationAuditReport',
        },
        isWorkflowsAuditReportFlagEnabled &&
          !isNewSystemAudit && {
            id: 'NAV_WORKFLOWS_AUDIT',
            to: `workflowsaudit`,
            permission: permissionConstants.VIEW_MENUITEM_SYSTEM_AUDIT_REPORT,
            selectorId: 'workflowsaudit',
          },
        isNewSystemAudit && {
          id: 'NAV_SYSTEM_AUDIT_REPORT_LEGACY',
          to: 'CocoonPlace:/xmatters/Reporting/ApplicationAuditReport/ApplicationAuditReport.do',
          permission: permissionConstants.VIEW_MENUITEM_SYSTEM_AUDIT_REPORT,
          isActive: () => subMenuId === 'ApplicationAuditReport', // Match all subroutes
          selectorId: 'ApplicationAuditReport',
        },
        isNewSystemAudit && {
          id: 'NAV_SYSTEM_AUDIT_REPORT',
          to: `systemaudit`,
          permission: permissionConstants.VIEW_MENUITEM_SYSTEM_AUDIT_REPORT,
          selectorId: 'systemaudit',
        },
        {
          id: 'NAV_SECURITY_AUDIT_REPORT',
          to: 'CocoonPlace:/xmatters/Reporting/SecurityAuditReport/SecurityAuditReport.do',
          permission: permissionConstants.VIEW_MENUITEM_SECURITY_AUDIT_REPORT,
          isActive: () => subMenuId === 'SecurityAuditReport', // Cocoon messes with the URL, compare subMenuId instead
          selectorId: 'SecurityAuditReport',
        },
        {
          id: 'NAV_WEB_SERVICE_AUDIT_REPORT',
          to: 'CocoonPlace:/xmatters/Reporting/WebServiceAuditReport/WebServiceAuditReport.do',
          permission: permissionConstants.VIEW_MENUITEM_SECURITY_AUDIT_REPORT,
          isActive: () => subMenuId === 'WebServiceAuditReport', // Cocoon messes with the URL, compare subMenuId instead
          selectorId: 'WebServiceAuditReport',
        },
      ],
    },
  ];

  return pruneItemsAndGroups(navItemGroups, permissions);
}

export default createSelector(
  [
    selectFeatureToggleMap,
    selectLoggedInUser,
    selectSubMenuId,
    getSignalsReportNavItem,
  ],
  getReportsNav,
);
